import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useIsAdmin, useIsBedrijfsadmin } from "../lib/permissions";
import { Modal, Field } from "./AdminCompaniesPage";
import { Plus, Trash, PencilSimple, User, ShieldCheck } from "@phosphor-icons/react";

export default function CompanyDetailPage() {
  const { companyId } = useParams();
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  const isBedrijfsadmin = useIsBedrijfsadmin();
  const canManage = isAdmin || (isBedrijfsadmin && user?.company_id === companyId);

  const [company, setCompany] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [bedrijfsadmin, setBedrijfsadmin] = useState(false);
  const [error, setError] = useState("");

  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [cName, setCName] = useState("");
  const [cDescription, setCDescription] = useState("");
  const [cError, setCError] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, cs] = await Promise.all([
        api.get(`/companies/${companyId}`),
        api.get("/job-coaches", { params: { company_id: companyId } }),
      ]);
      setCompany(c.data);
      setCoaches(cs.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, [companyId]);

  const openCreate = () => {
    setEditing(null); setName(""); setEmail(""); setPassword(""); setBedrijfsadmin(false);
    setError(""); setShowForm(true);
  };
  const openEdit = (c) => {
    setEditing(c); setName(c.name); setEmail(c.email); setPassword("");
    setBedrijfsadmin(c.is_bedrijfsadmin === true);
    setError(""); setShowForm(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault(); setError("");
    try {
      if (editing) {
        const payload = { name, email, is_bedrijfsadmin: bedrijfsadmin };
        if (password) payload.password = password;
        await api.put(`/job-coaches/${editing.id}`, payload);
      } else {
        await api.post("/job-coaches", {
          name, email, password, company_id: companyId, is_bedrijfsadmin: bedrijfsadmin,
        });
      }
      setShowForm(false); fetchAll();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Work coach ${c.name} verwijderen? Alle gekoppelde coachees worden ook verwijderd.`)) return;
    await api.delete(`/job-coaches/${c.id}`);
    fetchAll();
  };

  const openEditCompany = () => {
    setCName(company?.name || ""); setCDescription(company?.description || "");
    setCError(""); setShowCompanyForm(true);
  };

  const onSaveCompany = async (e) => {
    e.preventDefault(); setCError("");
    try {
      await api.put(`/companies/${companyId}`, { name: cName, description: cDescription });
      setShowCompanyForm(false); fetchAll();
    } catch (err) {
      setCError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <AppLayout
      title={company?.name || "Bedrijf"}
      breadcrumbs={
        isAdmin
          ? [{ label: "Bedrijven", to: "/admin" }, { label: company?.name || "..." }]
          : [{ label: company?.name || "..." }]
      }
    >
      {loading ? (
        <div className="text-zinc-500">Laden...</div>
      ) : (
        <>
          <div className="flex items-start justify-between mb-6 gap-4">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Work coaches</div>
              <p className="text-sm text-zinc-600 max-w-2xl">{company?.description}</p>
            </div>
            <div className="flex gap-2">
              {canManage && (
                <button
                  onClick={openEditCompany}
                  data-testid="edit-company-meta"
                  className="inline-flex items-center gap-2 border border-zinc-300 hover:border-zinc-900 px-3 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm transition-colors"
                >
                  <PencilSimple size={14} /> Bedrijf
                </button>
              )}
              {canManage && (
                <button
                  onClick={openCreate}
                  data-testid="add-job-coach-button"
                  className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
                >
                  <Plus size={16} /> Work coach toevoegen
                </button>
              )}
            </div>
          </div>

          {coaches.length === 0 ? (
            <div className="neuro-card p-12 text-center" data-testid="job-coaches-empty">
              <User size={32} className="mx-auto text-zinc-300" />
              <h3 className="font-display text-lg font-bold mt-3 tracking-tight">Nog geen work coaches</h3>
              <p className="text-sm text-zinc-500 mt-1">Voeg een work coach toe aan {company?.name}.</p>
            </div>
          ) : (
            <div className="neuro-card overflow-hidden" data-testid="job-coaches-table">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <Th>Naam</Th>
                    <Th>E-mail</Th>
                    <Th>Rol</Th>
                    <Th>Aangemaakt</Th>
                    {canManage && <th className="text-right px-4 py-3 text-xs uppercase tracking-widest text-zinc-500">Acties</th>}
                  </tr>
                </thead>
                <tbody>
                  {coaches.map((c) => (
                    <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`job-coach-row-${c.id}`}>
                      <Td className="font-medium">{c.name}</Td>
                      <Td>{c.email}</Td>
                      <Td>
                        {c.is_bedrijfsadmin ? (
                          <span className="inline-flex items-center gap-1 bg-[#1E88E5] text-white px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold rounded-sm">
                            <ShieldCheck size={12} weight="bold" /> Bedrijfsadmin
                          </span>
                        ) : (
                          <span className="inline-block bg-zinc-100 text-zinc-700 border border-zinc-200 px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold rounded-sm">
                            Work coach
                          </span>
                        )}
                      </Td>
                      <Td className="text-zinc-500">{new Date(c.created_at).toLocaleDateString()}</Td>
                      {canManage && (
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button onClick={() => openEdit(c)} data-testid={`edit-job-coach-${c.id}`} className="inline-flex items-center gap-1 text-zinc-600 hover:text-zinc-900 mr-3">
                            <PencilSimple size={16} /> Bewerken
                          </button>
                          {!(isBedrijfsadmin && c.id === user.id) && (
                            <button onClick={() => onDelete(c)} data-testid={`delete-job-coach-${c.id}`} className="inline-flex items-center gap-1 text-[#E53935] hover:text-[#E53935]/80">
                              <Trash size={16} /> Verwijderen
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showForm && (
        <Modal title={editing ? "Work coach bewerken" : "Nieuwe work coach"} onClose={() => setShowForm(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="job-coach-form">
            <Field label="Naam" value={name} onChange={setName} testid="job-coach-name-input" />
            <Field label="E-mailadres" value={email} onChange={setEmail} type="email" testid="job-coach-email-input" />
            <Field label={editing ? "Nieuw wachtwoord (leeg = niet wijzigen)" : "Wachtwoord"} type="password" value={password} onChange={setPassword} testid="job-coach-password-input" required={!editing} />
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1.5">Rol binnen bedrijf</label>
              <label className="flex items-start gap-3 border-2 border-zinc-200 rounded-sm p-3 cursor-pointer hover:border-zinc-400 transition-colors">
                <input
                  type="checkbox"
                  checked={bedrijfsadmin}
                  onChange={(e) => setBedrijfsadmin(e.target.checked)}
                  data-testid="job-coach-bedrijfsadmin-input"
                  className="mt-1 h-4 w-4 accent-zinc-900"
                />
                <div>
                  <div className="text-sm font-medium">Bedrijfsadmin status</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Geeft deze work coach het recht om alle work coaches en coachees binnen dit bedrijf te beheren.
                    Bedrijfsadmins kunnen het bedrijf niet verwijderen.
                  </div>
                </div>
              </label>
            </div>
            {error && <div className="text-sm text-[#E53935]" data-testid="job-coach-form-error">{error}</div>}
            <button type="submit" data-testid="job-coach-form-submit" className="w-full bg-zinc-900 text-white py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors">
              {editing ? "Opslaan" : "Toevoegen"}
            </button>
          </form>
        </Modal>
      )}

      {showCompanyForm && (
        <Modal title="Bedrijf bewerken" onClose={() => setShowCompanyForm(false)}>
          <form onSubmit={onSaveCompany} className="space-y-4" data-testid="company-edit-form">
            <Field label="Naam" value={cName} onChange={setCName} testid="company-edit-name-input" />
            <Field label="Beschrijving" value={cDescription} onChange={setCDescription} required={false} multiline testid="company-edit-description-input" />
            {cError && <div className="text-sm text-[#E53935]">{cError}</div>}
            <button type="submit" data-testid="company-edit-submit" className="w-full bg-zinc-900 text-white py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors">
              Opslaan
            </button>
          </form>
        </Modal>
      )}
    </AppLayout>
  );
}

function Th({ children }) { return <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-zinc-500 font-medium">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
